import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, FileText, FolderOpen, Trash2 } from 'lucide-react';
import { AttachmentViewer } from '@/components/attachment-viewer';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody } from '@/components/card';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  currentPeriod,
  LedgerToolbar,
  PeriodFilter,
  periodRange,
  SearchBox,
  SortMenu,
  type Period,
  type SortOption,
} from '@/components/ledger-toolbar';
import { useAttachmentFiles, useDeleteAttachmentFile } from '@/lib/accounting';
import { useDebouncedValue } from '@/lib/search';
import { formatBytes, formatDateFull } from '@/lib/format';
import type { AttachmentFileData } from '@/types/api';

const FILTERS = [
  { key: 'all', label: 'All files' },
  { key: 'orphaned', label: 'Orphaned' },
] as const;

const SORT_OPTIONS: SortOption[] = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A → Z' },
  { value: 'name_desc', label: 'Name Z → A' },
  { value: 'size_desc', label: 'Largest first' },
  { value: 'size_asc', label: 'Smallest first' },
];

export function FilesTab() {
  const [filter, setFilter] = useState<'all' | 'orphaned'>('all');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<Period>(currentPeriod);
  const [sort, setSort] = useState('date_desc');
  const [page, setPage] = useState(1);
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);
  const range = periodRange(period);
  const { data, isLoading } = useAttachmentFiles({
    orphaned: filter === 'orphaned',
    q: debouncedSearch,
    from: range.from,
    to: range.to,
    sort,
    page,
  });

  // Any filter change should return to the first page.
  function resetTo<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const viewing = viewIndex != null ? rows[viewIndex] : null;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Files</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Every receipt, check photo, and document attached anywhere in accounting. Orphaned files
          are ones whose entry or transaction was deleted — safe to remove here.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => resetTo(setFilter)(f.key)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <LedgerToolbar>
        <SearchBox value={search} onChange={resetTo(setSearch)} placeholder="Search file names…" />
        <div className="flex items-center gap-2">
          <PeriodFilter value={period} onChange={resetTo(setPeriod)} />
          <SortMenu options={SORT_OPTIONS} value={sort} onChange={resetTo(setSort)} />
        </div>
      </LedgerToolbar>

      <Card>
        <CardBody>
          {isLoading ? (
            <SkeletonList rows={5} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={FolderOpen}
              title={
                debouncedSearch || period.quarter !== 0
                  ? 'No files match'
                  : filter === 'orphaned'
                    ? 'No orphaned files'
                    : 'No files yet'
              }
              description={
                debouncedSearch || period.quarter !== 0
                  ? 'Nothing matches this search and period. Widen the date range or clear the search.'
                  : filter === 'orphaned'
                    ? 'Nothing is disconnected from its entry or transaction. Good.'
                    : 'Attach receipts and documents from a journal entry or a bank transaction and they will show up here.'
              }
            />
          ) : (
            <DataList>
              {rows.map((f, i) => (
                <FileRow key={f.id} file={f} onView={() => setViewIndex(i)} />
              ))}
            </DataList>
          )}
        </CardBody>
      </Card>

      {meta && meta.last_page > 1 ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {meta.current_page} of {meta.last_page} · {meta.total} total
          </span>
          <div className="flex gap-1.5">
            <Button
              variant="secondary"
              disabled={meta.current_page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={meta.current_page >= meta.last_page}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <AttachmentViewer
        file={viewing}
        onClose={() => setViewIndex(null)}
        position={viewIndex != null ? `${viewIndex + 1} of ${rows.length}` : undefined}
        onPrev={viewIndex != null && viewIndex > 0 ? () => setViewIndex(viewIndex - 1) : undefined}
        onNext={
          viewIndex != null && viewIndex < rows.length - 1
            ? () => setViewIndex(viewIndex + 1)
            : undefined
        }
      />
    </div>
  );
}

function FileRow({ file, onView }: { file: AttachmentFileData; onView: () => void }) {
  const del = useDeleteAttachmentFile();
  const confirm = useConfirm();
  const toast = useToast();
  const [, setSearchParams] = useSearchParams();

  function goToSource() {
    if (file.is_orphaned || file.linked_id == null) return;
    const tab = file.linked_type === 'bank_transaction' ? 'transactions' : 'journal';
    const params: Record<string, string> = { tab, focus: String(file.linked_id) };
    if (file.linked_status) params.status = file.linked_status;
    setSearchParams(params);
  }

  async function onDelete() {
    const ok = await confirm({
      title: 'Delete this file?',
      body: file.is_orphaned
        ? 'This file is no longer linked to anything. Deleting it removes it from storage for good.'
        : 'This removes the file from its entry and from storage permanently.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await del.mutateAsync(file.id);
      toast.success('File deleted');
    } catch {
      toast.error("Couldn't delete that file");
    }
  }

  return (
    <DataListRow>
      <button type="button" onClick={onView} className="shrink-0">
        {file.is_image ? (
          <img src={file.url} alt={file.file_name} className="size-9 rounded object-cover" />
        ) : (
          <div className="flex size-9 items-center justify-center rounded bg-muted">
            <FileText className="size-4 text-muted-foreground" />
          </div>
        )}
      </button>

      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onView}
          className="block max-w-full truncate text-left font-medium text-foreground hover:underline"
        >
          {file.file_name}
        </button>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{formatBytes(file.file_size)}</span>
          <span>·</span>
          <span>Added {formatDateFull(file.uploaded_at)}</span>
        </div>
      </div>

      {file.is_orphaned ? (
        <Badge tone="danger">
          <AlertTriangle className="mr-1 inline size-3" />
          Orphaned
        </Badge>
      ) : (
        <button
          type="button"
          onClick={goToSource}
          className="group flex min-w-0 max-w-[16rem] shrink-0 flex-col items-end text-right"
        >
          <span className="inline-flex items-center gap-1 text-sm text-foreground group-hover:underline">
            <span className="truncate">{file.linked_label ?? 'Linked'}</span>
            <ArrowUpRight className="size-3 shrink-0 text-muted-foreground" />
          </span>
          <span className="text-xs capitalize text-muted-foreground">
            {file.linked_type === 'bank_transaction' ? 'Transaction' : 'Journal entry'}
            {file.linked_date ? ` · ${formatDateFull(file.linked_date)}` : ''}
          </span>
        </button>
      )}

      <button
        type="button"
        onClick={onDelete}
        disabled={del.isPending}
        aria-label={`Delete ${file.file_name}`}
        className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive disabled:opacity-50"
      >
        <Trash2 className="size-3.5" />
      </button>
    </DataListRow>
  );
}
